/**
 * Universal Tooltip utility.
 *
 * Usage example (HTML):
 * <button
 *   data-universal-tooltip="This is a tooltip"
 *   data-universal-tooltip-position="top"           // top | bottom | left | right (default: bottom)
 *   data-universal-tooltip-animate="true"           // 'true' | 'false' (default: false)
 *   data-universal-tooltip-classname="custom-class"  // optional custom class (default: standard-universal-tooltip)
 * >Hover me</button>
 *
 * Styling: See SCSS at _patterns/99-global/tooltip/universalTooltip.scss
 */

interface UpdateUniversalTooltipEvent extends Event {
  detail: HTMLElement;
}

type Position = "top" | "bottom" | "left" | "right";

const selectors = {
  universalTooltip: ".universal-tooltip",
  universalTooltipText: ".universal-tooltip__text",
  universalTooltipArrow: ".universal-tooltip__arrow",
  tooltipTarget: "[data-universal-tooltip]",
};

const classes = {
  universalTooltip: "universal-tooltip",
  universalTooltipText: "universal-tooltip__text",
  universalTooltipArrow: "universal-tooltip__arrow",
  universalTooltipVisible: "universal-tooltip--visible",
  defaultUniversalTooltipClassname: "standard-universal-tooltip",
};

const attributes = {
  ariaHidden: "aria-hidden",
  animate: "data-universal-tooltip-animate",
  dataUniversalTooltip: "data-universal-tooltip",
  dataUniversalTooltipClassname: "data-universal-tooltip-classname",
  dataUniversalTooltipPosition: "data-universal-tooltip-position",
};

/**
 * Positions tooltip relative to target, choosing a best-fit position if needed.
 * @param {HTMLElement} tooltip Tooltip element (already in DOM)
 * @param {HTMLElement} target Target element being hovered/focused
 * @param {Position} [position='bottom'] Preferred starting position
 */
const positionTooltip = (
  tooltip: HTMLElement,
  target: HTMLElement,
  position: Position = "bottom",
) => {
  const targetRect = target.getBoundingClientRect();
  const documentRect = document.body.getBoundingClientRect();
  const arrow = tooltip.querySelector<HTMLElement>(
    selectors.universalTooltipArrow,
  );

  // Reset styles
  resetTooltipStyles(tooltip, arrow);

  const arrowSize = 10;
  const spacing = 13;

  // Adjust width/height to fit within the viewport *before* determining position
  adjustTooltipSize(tooltip, targetRect, documentRect);

  // Get the *actual* size of the tooltip *after* adjusting its width/height
  const adjustedTooltipRect = tooltip.getBoundingClientRect();

  const bestPosition = getBestPosition(
    position,
    targetRect,
    adjustedTooltipRect,
    documentRect,
    10,
  );

  switch (bestPosition) {
    case "top": {
      tooltip.style.top =
        targetRect.top - adjustedTooltipRect.height - spacing + "px";
      const center = targetRect.left + targetRect.width / 2;
      positionHorizontally(
        tooltip,
        center,
        adjustedTooltipRect,
        documentRect,
        arrow,
      );
      arrow.style.top = "auto";
      arrow.style.bottom = `-${arrowSize / 2}px`;
      break;
    }
    case "bottom": {
      tooltip.style.top = targetRect.bottom + spacing + "px";
      const center = targetRect.left + targetRect.width / 2;
      positionHorizontally(
        tooltip,
        center,
        adjustedTooltipRect,
        documentRect,
        arrow,
      );
      arrow.style.bottom = "auto";
      arrow.style.top = `-${arrowSize / 2}px`;
      break;
    }
    case "left": {
      tooltip.style.left =
        targetRect.left - adjustedTooltipRect.width - spacing + "px";
      const center = targetRect.top + targetRect.height / 2;
      positionVertically(
        tooltip,
        center,
        adjustedTooltipRect,
        documentRect,
        arrow,
      );
      arrow.style.left = "auto";
      arrow.style.right = `-${arrowSize / 2}px`;
      break;
    }
    case "right": {
      tooltip.style.left = targetRect.right + spacing + "px";
      const center = targetRect.top + targetRect.height / 2;
      positionVertically(
        tooltip,
        center,
        adjustedTooltipRect,
        documentRect,
        arrow,
      );
      arrow.style.right = "auto";
      arrow.style.left = `-${arrowSize / 2}px`;
      break;
    }
  }
};

/**
 * Clears inline positioning and sizing styles so new geometry can be calculated.
 */
const resetTooltipStyles = (tooltip: HTMLElement, arrow: HTMLElement) => {
  tooltip.style.top = "";
  tooltip.style.left = "";
  tooltip.style.right = "";
  tooltip.style.bottom = "";
  tooltip.style.width = "";
  tooltip.style.height = "";
  tooltip.style.maxWidth = "";
  tooltip.style.maxHeight = "";
  tooltip.style.whiteSpace = "";
  arrow.style.top = "";
  arrow.style.left = "";
  arrow.style.right = "";
  arrow.style.bottom = "";
};

/**
 * Determines the best available position for the tooltip, falling back through other sides.
 * @param {Position} preferredPosition Requested starting side.
 * @param {DOMRect} targetRect Bounding rect of the target element.
 * @param {DOMRect} tooltipRect Current tooltip size.
 * @param {DOMRect} documentRect Bounding rect of the document/body.
 * @param {number} threshold Minimum free space required to accept a side.
 * @returns {Position} Chosen side.
 */
const getBestPosition = (
  preferredPosition: Position,
  targetRect: DOMRect,
  tooltipRect: DOMRect,
  documentRect: DOMRect,
  threshold: number,
) => {
  const positions: Position[] = ["bottom", "top", "right", "left"]; // Order of preference
  const startIndex = positions.indexOf(preferredPosition);
  if (startIndex === -1) {
    console.warn(
      `Invalid preferred position: ${preferredPosition}. Defaulting to bottom.`,
    );
    return "bottom";
  }

  for (let i = 0; i < positions.length; i++) {
    const index = (startIndex + i) % positions.length;
    const position = positions[index];
    if (
      canPlaceTooltip(
        position,
        targetRect,
        tooltipRect,
        documentRect,
        threshold,
      )
    ) {
      return position;
    }
  }

  console.warn("No suitable position found.  Tooltip may be clipped.");
  return preferredPosition;
};

/**
 * Checks if tooltip can be fully placed on a given side with the threshold spacing.
 */
const canPlaceTooltip = (
  position: Position,
  targetRect: DOMRect,
  tooltipRect: DOMRect,
  documentRect: DOMRect,
  threshold: number,
) => {
  switch (position) {
    case "top":
      return targetRect.top >= tooltipRect.height + threshold;
    case "bottom":
      return (
        documentRect.height - targetRect.bottom >=
        tooltipRect.height + threshold
      );
    case "left":
      return targetRect.left >= tooltipRect.width + threshold;
    case "right":
      return (
        documentRect.width - targetRect.right >= tooltipRect.width + threshold
      );
    default:
      return false;
  }
};

/**
 * Constrains tooltip max width/height so it does not exceed available viewport space.
 */
const adjustTooltipSize = (
  tooltip: HTMLElement,
  targetRect: DOMRect,
  documentRect: DOMRect,
) => {
  const availableWidth = Math.min(
    targetRect.left + (documentRect.width - targetRect.right),
    documentRect.width,
  );
  tooltip.style.maxWidth = availableWidth + "px";

  const availableHeight = Math.min(
    targetRect.top + (documentRect.height - targetRect.bottom),
    documentRect.height,
  );
  tooltip.style.maxHeight = availableHeight + "px";
};

/**
 * Applies left/right positioning for top/bottom variants ensuring on-screen fit.
 * @param {HTMLElement} tooltip
 * @param {number} center Horizontal center to align to
 * @param {DOMRect} tooltipRect
 * @param {DOMRect} documentRect
 * @param {HTMLElement} arrow
 */
const positionHorizontally = (
  tooltip: HTMLElement,
  center: number,
  tooltipRect: DOMRect,
  documentRect: DOMRect,
  arrow: HTMLElement,
) => {
  const wouldOverflowLeft = center < tooltipRect.width / 2;
  const wouldOverflowRight =
    center + tooltipRect.width / 2 > documentRect.width;

  if (wouldOverflowLeft) {
    tooltip.style.left = "0px";
    tooltip.style.right = "auto";
  } else if (wouldOverflowRight) {
    tooltip.style.left = "auto";
    tooltip.style.right = "0px";
  } else {
    tooltip.style.left = center - tooltipRect.width / 2 + "px";
    tooltip.style.right = "auto";
  }

  const newTooltipRect = tooltip.getBoundingClientRect();
  const distanceToTooltip = center - newTooltipRect.left;
  arrow.style.left = distanceToTooltip - 5 + "px";
};

/**
 * Applies top/bottom positioning for left/right variants ensuring on-screen fit.
 * @param {HTMLElement} tooltip
 * @param {number} center Vertical center to align to
 * @param {DOMRect} tooltipRect
 * @param {DOMRect} documentRect
 * @param {HTMLElement} arrow
 */
const positionVertically = (
  tooltip: HTMLElement,
  center: number,
  tooltipRect: DOMRect,
  documentRect: DOMRect,
  arrow: HTMLElement,
) => {
  const wouldOverflowTop = center < tooltipRect.height / 2;
  const wouldOverflowBottom =
    center + tooltipRect.height / 2 > documentRect.height;

  if (wouldOverflowTop) {
    tooltip.style.top = "0px";
    tooltip.style.bottom = "auto";
  } else if (wouldOverflowBottom) {
    tooltip.style.top = "auto";
    tooltip.style.bottom = "0px";
  } else {
    tooltip.style.top = center - tooltipRect.height / 2 + "px";
    tooltip.style.bottom = "auto";
  }

  const newTooltipRect = tooltip.getBoundingClientRect();
  const distanceToTooltip = center - newTooltipRect.top;
  arrow.style.top = distanceToTooltip - 5 + "px";
};

/**
 * Creates the arrow element.
 */
const createTooltipArrow = () => {
  const arrowElement = document.createElement("div");
  arrowElement.classList.add(classes.universalTooltipArrow);

  return arrowElement;
};

/**
 * Creates the text span element.
 */
const createTextElement = () => {
  const arrowElement = document.createElement("span");
  arrowElement.classList.add(classes.universalTooltipText);

  return arrowElement;
};

/**
 * Builds the tooltip container and appends it to body (singleton pattern in practice).
 */
const createTooltip = () => {
  const tooltipElement = document.createElement("div");
  tooltipElement.classList.add(classes.universalTooltip);
  tooltipElement.setAttribute(attributes.ariaHidden, "true");
  tooltipElement.appendChild(createTooltipArrow());
  tooltipElement.appendChild(createTextElement());

  document.body.appendChild(tooltipElement);

  return tooltipElement;
};

/**
 * Shows (or reuses) the tooltip for a given target element.
 * @param {HTMLElement} target Element with data-universal-tooltip attribute.
 */
const showTooltip = (target: HTMLElement) => {
  const tooltipText = target.getAttribute(attributes.dataUniversalTooltip);
  const animate = target.getAttribute(attributes.animate) === "true";
  const className =
    target.getAttribute(attributes.dataUniversalTooltipClassname) ??
    classes.defaultUniversalTooltipClassname;
  const tooltipElement =
    document.querySelector<HTMLElement>(selectors.universalTooltip) ??
    createTooltip();
  const tooltipPosition =
    (target.getAttribute(
      attributes.dataUniversalTooltipPosition,
    ) as Position | null) ?? "bottom";

  tooltipElement.className = classes.universalTooltip;
  tooltipElement.querySelector(selectors.universalTooltipText).textContent =
    tooltipText;

  tooltipElement.classList.add(className);
  positionTooltip(tooltipElement, target, tooltipPosition);
  tooltipElement.classList.add(classes.universalTooltipVisible);

  // TODO add handling of animation for other tooltip positions
  if (tooltipPosition === "bottom" && animate) {
    tooltipElement.style.transform = "translateY(20px)";
  } else {
    tooltipElement.style.transform = "initial";
  }
};

/**
 * Initializes global listeners for hover / movement / scroll & manual update events.
 * Should be invoked once on page/app init.
 */
export const handleUniversalTooltip = () => {
  const hideTooltip = () => {
    let tooltipElement = document.querySelector<HTMLElement>(
      selectors.universalTooltip,
    );

    if (tooltipElement) {
      tooltipElement.classList.remove(classes.universalTooltipVisible);
      tooltipElement.style.transform = "initial";
    }
  };

  document.body.addEventListener("mousemove", (e) => {
    const eventTarget = e?.target as HTMLElement;
    const target = eventTarget?.closest<HTMLElement>(selectors.tooltipTarget);

    if (target) {
      showTooltip(target);
    } else {
      hideTooltip();
    }
  });

  window.addEventListener(
    "updateUniversalTooltip",
    (e: UpdateUniversalTooltipEvent) => {
      const target = e?.detail;
      const targetElement = target?.classList.contains(selectors.tooltipTarget)
        ? target
        : target?.closest<HTMLElement>(selectors.tooltipTarget);

      if (targetElement) {
        showTooltip(targetElement);
      }
    },
  );
  window.addEventListener("scroll", () => hideTooltip(), true);
};
